import { FORECAST_MODELS } from '@app/common/static-data/models';

// Jua brand colors and compatible color palette
export const JUA_BRAND_ORANGE = '#FABD8F';
export const JUA_BRAND_PINK = '#E34290';
export const JUA_BRAND_CYAN = '#0AB8DC';
export const JUA_BRAND_PURPLE = '#752D88';
export const JUA_BRAND_LIGHT_CYAN = '#B6EAF5';
export const JUA_BRAND_LIGHT_ORANGE = '#FEF0E6';
export const JUA_BRAND_LIGHT_PURPLE = '#DBC9E0';
export const JUA_BRAND_LIGHT_PINK = '#F7CCE1';
export const DARK_CYAN = '#07788f';
export const DARK_PURPLE = '#5A266F';
export const DARK_PINK = '#9E145A';
export const DARK_ORANGE = '#FF8326';

// Jua-compatible color palette with good contrast and accessibility
export const JUA_COLOR_PALETTE = [
  JUA_BRAND_PINK, // Jua brand pink - for primary jua models
  '#2563EB', // Blue - strong contrast
  '#DC2626', // Red - good visibility
  '#059669', // Green - accessible
  '#14b8a6', // Teal
  '#7C3AED', // Purple - distinct
  '#EA580C', // Orange - warm tone
  '#0891B2', // Cyan - cool tone
  '#BE185D', // Deep pink - complement to brand pink
  '#00BFFF', // Gold - replaces dark gray for better visibility
  '#6366F1', // Indigo - cool accent
  '#84CC16', // Lime - bright accent
  '#F59E0B', // Amber - warm accent
  '#EF4444', // Bright red - alert color
  '#10B981', // Emerald - success color
  '#8B5CF6', // Violet - creative accent
  '#00FF00', // Lime - bright accent
  '#FF00FF', // Amber - warm accent
  '#FF0000', // Bright red - alert color
  '#00FF00', // Emerald - success color
  '#0000FF', // Indigo - cool accent
  '#FF00FF', // Violet - creative accent
  JUA_BRAND_ORANGE,
  JUA_BRAND_PURPLE,
  JUA_BRAND_CYAN,
  JUA_BRAND_LIGHT_CYAN,
  JUA_BRAND_LIGHT_PURPLE,
  JUA_BRAND_LIGHT_PINK,
];

// Model-specific color assignments for consistency
export const MODEL_DEFAULT_COLOR_MAP: Record<string, string> = {
  ept2_e: JUA_BRAND_ORANGE,
  ept1_5: JUA_BRAND_CYAN, // Cyan
  ept1_5_early: JUA_BRAND_LIGHT_CYAN, // Light cyan
  ept2: JUA_BRAND_PINK, // Changed from CYAN to PINK
  ept2_early: JUA_BRAND_LIGHT_CYAN,
  ept2_reasoning: '#2563EB', // Bright blue
  aurora: JUA_COLOR_PALETTE[14], // Lime
  aifs: JUA_COLOR_PALETTE[13], // Bright red
  ecmwf_aifs025_single: JUA_COLOR_PALETTE[5], // Purple
  ecmwf_aifs025_ensemble: JUA_COLOR_PALETTE[1], // Deep Blue
  gfs_graphcast025: JUA_COLOR_PALETTE[10], // Indigo
  ecmwf_ifs_single: JUA_COLOR_PALETTE[12], // Blue
  ecmwf_ifs025_ensemble: JUA_COLOR_PALETTE[2], // Red
  gfs_global_ensemble: JUA_COLOR_PALETTE[6], // Orange
  meteofrance_arome_france_hd: JUA_COLOR_PALETTE[7], // Cyan
  icon_eu: JUA_COLOR_PALETTE[9], // Gold (was dark gray)
  noaa_gfs_single: '#EA580C', // Orange
};

// Model-specific light mode color assignments
export const MODEL_COLOR_MAP_LIGHT_MODE: Record<string, string> = {
  ept2_e: JUA_BRAND_ORANGE,
  ept1_5: JUA_BRAND_CYAN,
  ept1_5_early: JUA_BRAND_LIGHT_CYAN,
  ept2: JUA_BRAND_PINK, // Changed from CYAN to PINK
  ept2_early: JUA_BRAND_PURPLE,
  ept2_reasoning: '#2563EB', // Bright blue - intelligent/reasoning
  ept2_rr: DARK_PINK,
  ept2_hrrr: '#7C3AED',
  noaa_gfs_single: '#EA580C', // Orange
};

// Model-specific dark mode color assignments
export const MODEL_COLOR_MAP_DARK_MODE: Record<string, string> = {
  ept2_e: JUA_BRAND_ORANGE,
  ept1_5: JUA_BRAND_CYAN,
  ept1_5_early: JUA_BRAND_LIGHT_CYAN,
  ept2: JUA_BRAND_PINK, // Changed from CYAN to PINK
  ept2_early: JUA_BRAND_PURPLE,
  ept2_reasoning: '#3B82F6', // Slightly lighter blue for dark mode
  ept2_rr: DARK_PINK,
  ept2_hrrr: '#7C3AED',
  noaa_gfs_single: '#F97316', // Slightly lighter orange for dark mode
};

/**
 * Get a consistent color for a model across all charts and configurations
 * @param modelId - The model identifier
 * @param fallbackIndex - Fallback index if model not in predefined map
 * @param theme - Current theme ('light' | 'dark'), defaults to 'light'
 */
export function getModelColor(
  modelId: string,
  fallbackIndex: number,
  theme: 'light' | 'dark' = 'light',
): string {
  // Return predefined color if it exists
  if (theme === 'dark' && MODEL_COLOR_MAP_DARK_MODE[modelId]) {
    return MODEL_COLOR_MAP_DARK_MODE[modelId];
  } else if (MODEL_COLOR_MAP_LIGHT_MODE[modelId]) {
    return MODEL_COLOR_MAP_LIGHT_MODE[modelId];
  } else if (MODEL_DEFAULT_COLOR_MAP[modelId]) {
    return MODEL_DEFAULT_COLOR_MAP[modelId];
  }

  // Fall back to palette cycling for unknown models
  return JUA_COLOR_PALETTE[fallbackIndex % JUA_COLOR_PALETTE.length];
}

/**
 * Check if a model is a Jua model
 */
export function isJuaModel(modelId: string): boolean {
  const model = FORECAST_MODELS.find((m) => m.id === modelId);
  return model?.provider === 'jua';
}
